import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import { fmtRs, fmtDate, scoreBand } from '../lib/format';
import { scoreMeta, priorityActions, computeSipNeeded, computeLumpsumNeeded, computeSipAtRate, ILLUSTRATIVE_RATE } from '../lib/scoring';
import { DISCLAIMERS, PRODUCT_CATEGORIES } from '../lib/disclaimers';

// Colors
const C = {
  indigo: '#1F135A',
  green: '#60B74F',
  cyan: '#00B4E2',
  amber: '#EF9F27',
  red: '#E24B4A',
  bg: '#F4F3FB',
  border: '#E0DFF4',
  muted: '#6b7280'
};

const styles = StyleSheet.create({
  page: { padding: 0, fontSize: 9, fontFamily: 'Helvetica', color: C.indigo },
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.indigo, color: 'white', padding: 14, borderLeftWidth: 5, borderLeftColor: C.green },
  headerLogo: { width: 32, height: 32, marginRight: 10, backgroundColor: 'white', padding: 2 },
  headerTitle: { fontSize: 14, fontWeight: 700 },
  headerSub: { fontSize: 8, opacity: 0.85, marginTop: 2 },
  headerRight: { marginLeft: 'auto', alignItems: 'flex-end' },
  section: { padding: 16, paddingBottom: 6 },
  h2: { fontSize: 12, fontWeight: 700, color: C.indigo, marginBottom: 6 },
  h3: { fontSize: 10, fontWeight: 700, color: C.indigo, marginBottom: 4 },
  row: { flexDirection: 'row' },
  card: { border: `1pt solid ${C.border}`, borderRadius: 4, padding: 8, marginBottom: 6, backgroundColor: 'white' },
  scoreBig: { fontSize: 36, fontWeight: 700 },
  tableHead: { flexDirection: 'row', backgroundColor: C.bg, padding: 5, borderBottom: `1pt solid ${C.border}`, fontWeight: 700, fontSize: 8 },
  tableRow: { flexDirection: 'row', padding: 5, borderBottom: `0.5pt solid ${C.border}`, fontSize: 8 },
  disclaimerBox: { border: `1pt solid ${C.border}`, borderRadius: 3, padding: 6, marginBottom: 4, backgroundColor: '#FAFAFF' },
  footer: { position: 'absolute', bottom: 0, left: 0, right: 0, padding: 8, borderTop: `1pt solid ${C.border}`, backgroundColor: C.bg, fontSize: 7, color: C.muted, flexDirection: 'row', justifyContent: 'space-between' },
  clientBar: { flexDirection: 'row', backgroundColor: C.bg, padding: 10, borderBottom: `1pt solid ${C.border}` },
  muted: { color: C.muted, fontSize: 8 },
  greenText: { color: C.green, fontWeight: 700 },
  redText: { color: C.red, fontWeight: 700 },
  amberText: { color: C.amber, fontWeight: 700 }
});

const scoreTextColor = (s) => s >= 70 ? C.green : s >= 40 ? C.amber : C.red;

const Header = () => (
  <View style={styles.header} fixed>
    <Image src="/assets/akta-logo.png" style={styles.headerLogo} />
    <View>
      <Text style={styles.headerTitle}>Akta Wealth</Text>
      <Text style={styles.headerSub}>Client Financial Awareness Report</Text>
    </View>
    <View style={styles.headerRight}>
      <Text style={{ fontSize: 9, fontWeight: 700 }}>ARN-47282</Text>
      <Text style={{ fontSize: 7, opacity: 0.85 }}>Not a SEBI Registered Investment Adviser</Text>
    </View>
  </View>
);

const Footer = ({ page, total }) => (
  <View style={styles.footer} fixed>
    <Text>AMFI-registered Mutual Fund Distributor · ARN-47282 · Mutual Fund investments are subject to market risks.</Text>
    <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} />
  </View>
);

// Diagonal semi-transparent DRAFT watermark rendered on every page when session is not signed.
const DraftWatermark = () => (
  <View
    fixed
    style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: -1,
    }}
  >
    <Text
      style={{
        fontSize: 160,
        fontWeight: 700,
        color: '#9AA0B0',
        opacity: 0.12,
        transform: 'rotate(-32deg)',
        letterSpacing: 12,
      }}
    >
      DRAFT
    </Text>
  </View>
);

const ClientBar = ({ client, session, rm }) => (
  <View style={styles.clientBar}>
    <View style={{ flex: 1 }}>
      <Text style={styles.muted}>CLIENT</Text>
      <Text style={{ fontSize: 12, fontWeight: 700 }}>{client?.name}</Text>
      <Text style={styles.muted}>{client?.city} · PAN ••••{client?.pan_last4}</Text>
    </View>
    <View style={{ flex: 1 }}>
      <Text style={styles.muted}>SESSION DATE</Text>
      <Text style={{ fontSize: 10, fontWeight: 700 }}>{fmtDate(session?.session_date)}</Text>
    </View>
    <View style={{ flex: 1 }}>
      <Text style={styles.muted}>RELATIONSHIP MANAGER</Text>
      <Text style={{ fontSize: 10, fontWeight: 700 }}>{rm?.name}</Text>
      <Text style={styles.muted}>EUIN {rm?.euin}</Text>
    </View>
  </View>
);

export const MRIReportDocument = ({ client, session, scores, rm }) => {
  const actions = priorityActions(session, scores);
  const isDraft = (session?.status || 'draft') !== 'completed' && (session?.status || 'draft') !== 'signed';
  const scenarioShown = session?.scenario_shown || 'both';
  const showConservative = scenarioShown === 'both' || scenarioShown === 'conservative';
  const showIllustrative = scenarioShown === 'both' || scenarioShown === 'illustrative';
  const scenarioNote = scenarioShown === 'conservative'
    ? 'SIP scenario at Conservative 10% p.a. Illustrative only. Not guaranteed. See D-8.'
    : scenarioShown === 'illustrative'
      ? 'SIP scenario at AMFI Illustrative 12.5% p.a. Illustrative only. Not guaranteed. See D-8.'
      : 'SIP scenarios at Conservative (10%) and AMFI Illustrative (12.5%) p.a. Capped at 12.5% \u2014 the AMFI-permitted illustrative rate. Not guaranteed. See D-8.';

  // SIP step-up projection
  const monthlySip = Number(session.monthly_sip) || 0;
  const stepup = 0.10;
  const returnR = ILLUSTRATIVE_RATE;
  const yearsList = [3, 5, 7, 10, 15, Math.max(15, (Number(session.retire_age) || 60) - (Number(session.age) || 30))];
  const proj = yearsList.map((y) => {
    let corpus = 0; let sip = monthlySip;
    for (let yr = 1; yr <= y; yr++) {
      corpus = (corpus + sip * 12) * (1 + returnR);
      sip = sip * (1 + stepup);
    }
    return { year: y, corpus: Math.round(corpus) };
  });

  return (
    <Document>
      {/* PAGE 1 */}
      <Page size="A4" style={styles.page}>
        {isDraft && <DraftWatermark />}
        <Header />
        <ClientBar client={client} session={session} rm={rm} />

        <View style={styles.section}>
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <View style={{ ...styles.card, flex: 1, alignItems: 'center' }}>
              <Text style={styles.muted}>OVERALL MRI SCORE</Text>
              <Text style={{ ...styles.scoreBig, color: scoreTextColor(scores.overall), marginTop: 4 }}>{scores.overall}</Text>
              <Text style={{ fontSize: 9, color: C.muted }}>out of 100 · {scoreBand(scores.overall)}</Text>
              <Text style={{ fontSize: 7, color: C.muted, marginTop: 6, textAlign: 'center' }}>This score is an awareness indicator based on client-provided data. It is not an investment rating or financial health certificate.</Text>
            </View>
            <View style={{ ...styles.card, flex: 2 }}>
              <Text style={styles.h3}>Health Band</Text>
              <View style={{ flexDirection: 'row', gap: 3, marginTop: 6 }}>
                {['Critical', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'].map((b, i) => {
                  const active = scoreBand(scores.overall) === b;
                  const colors = [C.red, C.red, C.amber, C.amber, C.green, C.green];
                  return (
                    <View key={b} style={{ flex: 1, backgroundColor: active ? colors[i] : C.border, borderRadius: 2, padding: 4, alignItems: 'center' }}>
                      <Text style={{ fontSize: 7, color: active ? 'white' : C.muted, fontWeight: active ? 700 : 400 }}>{b}</Text>
                    </View>
                  );
                })}
              </View>
              <View style={{ marginTop: 10, gap: 3 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Risk Profile</Text><Text style={{ fontWeight: 700 }}>{scores.riskLabel}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Net Worth</Text><Text style={{ fontWeight: 700 }}>{fmtRs(scores.netWorthValue, { compact: true })}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Monthly Surplus</Text><Text style={{ fontWeight: 700 }}>{fmtRs(scores.monthlySurplus)}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Emergency Cover</Text><Text style={{ fontWeight: 700 }}>{scores.efMonths.toFixed(1)} months</Text></View>
              </View>
            </View>
          </View>

          <Text style={{ ...styles.h2, marginTop: 12 }}>9-Metric Diagnostic Summary</Text>
          <View style={styles.tableHead}>
            <Text style={{ flex: 3 }}>Diagnostic</Text>
            <Text style={{ flex: 5 }}>Key Finding</Text>
            <Text style={{ flex: 1.2, textAlign: 'right' }}>Score</Text>
            <Text style={{ flex: 1.5, textAlign: 'right' }}>Rating</Text>
          </View>
          {scoreMeta.map((m) => {
            const v = scores[m.key];
            const finding = m.key === 'insurance' ? `Life cover gap: ${fmtRs(scores.lcGap, { compact: true })}` :
              m.key === 'emergency' ? `${scores.efMonths.toFixed(1)} months of expenses covered` :
              m.key === 'goals' ? `SIP gap: ${fmtRs(scores.sipGap)}/mo` :
              m.key === 'retirement' ? `Corpus gap: ${fmtRs(scores.retireGap, { compact: true })}` :
              m.key === 'netWorth' ? `Net worth: ${fmtRs(scores.netWorthValue, { compact: true })}` :
              m.key === 'risk' ? `${scores.riskLabel} profile` :
              m.key === 'tax' ? `Deductions used: 80C, 80D` :
              m.key === 'portfolio' ? `Alignment review needed` : '';
            return (
              <View key={m.key} style={styles.tableRow}>
                <Text style={{ flex: 3, fontWeight: 700 }}>{m.label}</Text>
                <Text style={{ flex: 5, color: C.muted }}>{finding}</Text>
                <Text style={{ flex: 1.2, textAlign: 'right', color: scoreTextColor(v), fontWeight: 700 }}>{v}</Text>
                <Text style={{ flex: 1.5, textAlign: 'right', color: scoreTextColor(v) }}>{scoreBand(v)}</Text>
              </View>
            );
          })}
        </View>

        <Footer />
      </Page>

      {/* PAGE 2 */}
      <Page size="A4" style={styles.page}>
        {isDraft && <DraftWatermark />}
        <Header />
        <View style={styles.section}>
          <Text style={styles.h2}>Priority Action Plan</Text>
          <Text style={{ ...styles.muted, marginBottom: 6 }}>Top actions auto-generated based on gap analysis.</Text>
          {actions.map((a, i) => (
            <View key={i} style={{ ...styles.card, flexDirection: 'row', gap: 8 }}>
              <View style={{ width: 20, height: 20, borderRadius: 10, backgroundColor: C.indigo, alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{ color: 'white', fontSize: 9, fontWeight: 700 }}>{i + 1}</Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontWeight: 700, fontSize: 9 }}>{a.title}</Text>
                <Text style={{ ...styles.muted, marginTop: 2 }}>{a.why}</Text>
                <View style={{ flexDirection: 'row', marginTop: 3, gap: 8 }}>
                  <Text style={{ fontSize: 7, color: C.indigo }}>Timeline: <Text style={{ fontWeight: 700 }}>{a.timeline}</Text></Text>
                  <Text style={{ fontSize: 7, color: C.indigo }}>Category: <Text style={{ fontWeight: 700 }}>{a.category}</Text></Text>
                </View>
              </View>
            </View>
          ))}

          <Text style={{ ...styles.h2, marginTop: 6 }}>SIP Step-Up Projection (Illustrative)</Text>
          <Text style={{ fontSize: 7, color: C.muted, marginBottom: 4 }}>Assumes 12.5% p.a. return (AMFI-permitted illustrative rate), 10% annual SIP step-up. Illustrative only. Not guaranteed. Actual returns may vary. [D-8]</Text>
          <View style={styles.tableHead}>
            <Text style={{ flex: 1 }}>Year</Text>
            <Text style={{ flex: 2, textAlign: 'right' }}>Projected Corpus</Text>
          </View>
          {proj.map((p) => (
            <View key={p.year} style={styles.tableRow}>
              <Text style={{ flex: 1 }}>Year {p.year}</Text>
              <Text style={{ flex: 2, textAlign: 'right', fontWeight: 700 }}>{fmtRs(p.corpus, { compact: true })}</Text>
            </View>
          ))}
        </View>
        <Footer />
      </Page>

      {/* PAGE 3 */}
      <Page size="A4" style={styles.page}>
        {isDraft && <DraftWatermark />}
        <Header />
        <View style={styles.section}>
          <Text style={styles.h2}>Detailed Diagnostic Findings</Text>

          <View style={styles.card}>
            <Text style={styles.h3}>Goal Awareness Checklist</Text>
            <Text style={{ fontSize: 7, color: C.muted, marginBottom: 4 }}>Not a financial plan. See D-2.</Text>
            {(session.goals || []).length > 0 ? (
              <>
                <View style={styles.tableHead}>
                  <Text style={{ flex: 2.5 }}>Goal</Text>
                  <Text style={{ flex: 1.6, textAlign: 'right' }}>Target</Text>
                  <Text style={{ flex: 0.7, textAlign: 'right' }}>Yrs</Text>
                  {showConservative && <Text style={{ flex: 1.6, textAlign: 'right', color: C.amber }}>@10%</Text>}
                  {showIllustrative && <Text style={{ flex: 1.6, textAlign: 'right' }}>@12.5%</Text>}
                  <Text style={{ flex: 1.8, textAlign: 'right' }}>Lumpsum</Text>
                  <Text style={{ flex: 1.4, textAlign: 'right' }}>Priority</Text>
                </View>
                {(session.goals || []).map((g, i) => {
                  const lump = computeLumpsumNeeded(g);
                  return (
                    <View key={i} style={styles.tableRow}>
                      <Text style={{ flex: 2.5 }}>{g.type}</Text>
                      <Text style={{ flex: 1.6, textAlign: 'right' }}>{fmtRs(g.amount, { compact: true })}</Text>
                      <Text style={{ flex: 0.7, textAlign: 'right' }}>{g.years}</Text>
                      {showConservative && <Text style={{ flex: 1.6, textAlign: 'right', color: C.amber }}>{fmtRs(computeSipAtRate(g, 0.10))}</Text>}
                      {showIllustrative && <Text style={{ flex: 1.6, textAlign: 'right', fontWeight: 700 }}>{fmtRs(computeSipNeeded(g))}</Text>}
                      <Text style={{ flex: 1.8, textAlign: 'right', fontWeight: 700 }}>{fmtRs(lump, { compact: true })}</Text>
                      <Text style={{ flex: 1.4, textAlign: 'right' }}>{g.priority}</Text>
                    </View>
                  );
                })}
                <Text style={{ fontSize: 7, color: C.muted, marginTop: 4 }}>{scenarioNote}</Text>
              </>
            ) : <Text style={styles.muted}>No goals recorded.</Text>}
          </View>

          <View style={{ flexDirection: 'row', gap: 8 }}>
            <View style={{ ...styles.card, flex: 1 }}>
              <Text style={styles.h3}>Insurance Gap</Text>
              <View style={{ gap: 3 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>10x income benchmark</Text><Text style={{ fontWeight: 700 }}>{fmtRs(scores.lcNeeded, { compact: true })}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Life cover in place</Text><Text>{fmtRs(session.life_cover, { compact: true })}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Life gap</Text><Text style={scores.lcGap > 0 ? styles.redText : styles.greenText}>{fmtRs(scores.lcGap, { compact: true })}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Health cover</Text><Text>{fmtRs(session.health_cover, { compact: true })}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Term plan</Text><Text>{session.has_term ? 'Yes' : 'No'}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Critical illness</Text><Text>{session.has_critical ? 'Yes' : 'No'}</Text></View>
              </View>
            </View>

            <View style={{ ...styles.card, flex: 1 }}>
              <Text style={styles.h3}>Retirement Gap</Text>
              <View style={{ gap: 3 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Corpus needed (25x)</Text><Text style={{ fontWeight: 700 }}>{fmtRs(scores.corpusNeeded, { compact: true })}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Projected corpus</Text><Text>{fmtRs(scores.projectedCorpus, { compact: true })}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Gap</Text><Text style={scores.retireGap > 0 ? styles.redText : styles.greenText}>{fmtRs(scores.retireGap, { compact: true })}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Years to retire</Text><Text>{scores.yearsToRetire}</Text></View>
              </View>
            </View>
          </View>

          <View style={{ flexDirection: 'row', gap: 8 }}>
            <View style={{ ...styles.card, flex: 1 }}>
              <Text style={styles.h3}>Net Worth</Text>
              <View style={{ gap: 3 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Total assets</Text><Text>{fmtRs(scores.totalAssets, { compact: true })}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Loans</Text><Text>{fmtRs(session.total_loans, { compact: true })}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Net worth</Text><Text style={{ fontWeight: 700 }}>{fmtRs(scores.netWorthValue, { compact: true })}</Text></View>
              </View>
            </View>
            <View style={{ ...styles.card, flex: 1 }}>
              <Text style={styles.h3}>Emergency & Portfolio</Text>
              <View style={{ gap: 3 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Liquid savings</Text><Text>{fmtRs(session.liquid_savings, { compact: true })}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Cover</Text><Text style={{ fontWeight: 700 }}>{scores.efMonths.toFixed(1)} months</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Equity allocation</Text><Text>{session.equity_pct || '—'}</Text></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={styles.muted}>Last review</Text><Text>{session.last_rebalance || '—'}</Text></View>
              </View>
            </View>
          </View>

          <View style={styles.card}>
            <Text style={styles.h3}>Family Protection Checklist</Text>
            <View style={{ gap: 2 }}>
              <Text style={{ fontSize: 8 }}>• Pure term life insurance: <Text style={session.has_term ? styles.greenText : styles.redText}>{session.has_term ? 'In place' : 'To arrange'}</Text></Text>
              <Text style={{ fontSize: 8 }}>• Critical illness cover: <Text style={session.has_critical ? styles.greenText : styles.redText}>{session.has_critical ? 'In place' : 'To arrange'}</Text></Text>
              <Text style={{ fontSize: 8 }}>• Family floater health cover ≥ ₹10L: <Text style={(Number(session.health_cover)||0) >= 1000000 ? styles.greenText : styles.amberText}>{(Number(session.health_cover)||0) >= 1000000 ? 'Adequate' : 'Review'}</Text></Text>
              <Text style={{ fontSize: 8 }}>• Registered Will: <Text style={styles.amberText}>Recommended — zero cost, highest impact</Text></Text>
            </View>
          </View>
        </View>
        <Footer />
      </Page>

      {/* PAGE 4 */}
      <Page size="A4" style={styles.page}>
        {isDraft && <DraftWatermark />}
        <Header />
        <View style={styles.section}>
          <Text style={styles.h2}>Product Category Awareness</Text>
          <Text style={{ fontSize: 7, color: C.muted, marginBottom: 4 }}>Categories only. No specific scheme names. See D-5.</Text>
          <View style={styles.tableHead}>
            <Text style={{ flex: 3 }}>Category</Text>
            <Text style={{ flex: 4 }}>Typical Purpose</Text>
            <Text style={{ flex: 2 }}>Horizon</Text>
          </View>
          {PRODUCT_CATEGORIES.map((p) => (
            <View key={p.category} style={styles.tableRow}>
              <Text style={{ flex: 3, fontWeight: 700 }}>{p.category}</Text>
              <Text style={{ flex: 4 }}>{p.purpose}</Text>
              <Text style={{ flex: 2 }}>{p.horizon}</Text>
            </View>
          ))}

          <Text style={{ ...styles.h2, marginTop: 10 }}>Mandatory Disclaimers</Text>
          {DISCLAIMERS.map((d) => (
            <View key={d.id} style={styles.disclaimerBox} wrap={false}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
                <Text style={{ fontWeight: 700, fontSize: 8 }}>{d.id} — {d.title}</Text>
                <Text style={{ fontSize: 7, color: d.level === 'HIGH' ? C.red : d.level === 'MEDIUM' ? C.amber : C.muted, fontWeight: 700 }}>{d.level}</Text>
              </View>
              <Text style={{ fontSize: 7, color: C.muted, lineHeight: 1.4 }}>{d.text}</Text>
            </View>
          ))}

          <View style={{ flexDirection: 'row', gap: 20, marginTop: 12 }}>
            <View style={{ flex: 1, borderTop: `1pt solid ${C.indigo}`, paddingTop: 4 }}>
              <Text style={{ fontSize: 8, fontWeight: 700 }}>Client Signature</Text>
              <Text style={styles.muted}>{client?.name}</Text>
              <Text style={styles.muted}>Date: __________________</Text>
            </View>
            <View style={{ flex: 1, borderTop: `1pt solid ${C.indigo}`, paddingTop: 4 }}>
              <Text style={{ fontSize: 8, fontWeight: 700 }}>Authorised Signatory</Text>
              <Text style={styles.muted}>{rm?.name} · EUIN {rm?.euin}</Text>
              <Text style={styles.muted}>Akta Wealth · ARN-47282</Text>
            </View>
          </View>
        </View>
        <Footer />
      </Page>
    </Document>
  );
};

export default MRIReportDocument;
